import React, { useState, useEffect } from 'react';
import {
  Mic,
  FileText,
  CreditCard,
  Shield,
  Smartphone,
  Rocket,
  ChevronDown,
  Search,
  LogIn,
  Settings,
  HelpCircle,
  Mail
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/buttons/BackButton';
import HeaderNoAuth from '../components/Header/HeaderNoAuth';
import Card from '../components/Card/Card';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import Button from '../components/buttons/Button';
import './FAQ.css';

const faqData = [
  {
    category: 'Prise en main & accès',
    icon: LogIn,
    color: '#1abc9c',
    questions: [
      {
        q: "Comment créer mon compte voxia ?",
        a: "L'inscription se fait par téléphone. Indiquez votre numéro de téléphone et vous recevrez un code PIN par SMS pour vérifier votre identité."
      },
      {
        q: "Je n'ai pas reçu le code de vérification, que faire ?",
        a: "Vérifiez que votre numéro de téléphone est correct, puis cliquez sur « Renvoyer le code ». Si le problème persiste, contactez le support via la page Contact."
      },
      {
        q: "Puis-je utiliser voxia sans connexion internet ?",
        a: "Oui, l'enregistrement fonctionne hors ligne et sera automatiquement synchronisé et transcrit dès que vous retrouvez du réseau."
      },
      {
        q: "Comment activer la reconnaissance biométrique ?",
        a: "Une fois authentifié par téléphone, vous pouvez ajouter votre appareil comme 'appareil de confiance' et activer la reconnaissance biométrique (Face ID, Touch ID, empreinte digitale) pour un accès rapide. Important : les données biométriques restent uniquement sur votre appareil et ne sont jamais transmises à nos serveurs."
      }
    ]
  },
  {
    category: 'Dictée et enregistrement',
    icon: Mic,
    color: '#e74c3c',
    questions: [
      {
        q: "Comment démarrer, mettre en pause et reprendre une dictée ?",
        a: "Appuyez sur le bouton microphone rouge pour démarrer. L'application attend automatiquement que vous commenciez à parler. Appuyez de nouveau pour arrêter et transcrire votre dictée."
      },
      {
        q: "Quelle est la différence entre Dictée Live et Dictée standard ?",
        a: "La Dictée Live (application bureau uniquement, plan Gold+) transcrit instantanément et insère le texte directement dans votre logiciel (email, Word, dossier patient). La Dictée standard analyse tout votre texte pour une meilleure qualité, idéale pour les rapports complets."
      },
      {
        q: "Puis-je importer des enregistrements depuis mon dictaphone physique ?",
        a: "Oui (application bureau uniquement), voxia détecte automatiquement les dictaphones Philips connectés par câble USB. Activez 'Récupération automatique' dans Réglages > Dictaphone pour que voxia récupère automatiquement vos enregistrements."
      },
      {
        q: "Comment changer la langue de dictée ?",
        a: "Allez dans Réglages > Préférences. 'Langue de dictée' = langue que vous parlez. 'Langue des rapports' = langue du document généré. Plans Silver/Gold/Enterprise peuvent utiliser des langues différentes (ex: dicter en français, rapport en allemand). Plan Bronze : les deux langues sont synchronisées."
      },
      {
        q: "Pourquoi la transcription semble incorrecte ?",
        a: "Pour améliorer la qualité : 1) Evitez les écouteurs Bluetooth, 2) Placez le micro/téléphone à 10-20cm de votre bouche, 3) Évitez les endroits bruyants, 4) Parlez clairement, 5) Sélectionnez le bon modèle de rapport avant de dicter."
      }
    ]
  },
  {
    category: 'Rapports & documents',
    icon: FileText,
    color: '#3498db',
    questions: [
      {
        q: "Comment ajouter des images ou résultats d'examen à un rapport ?",
        a: "Utilisez la fonction Annexe : prenez une photo, importez un fichier (PDF, image, Word), ou collez du texte. Voxia extrait automatiquement le texte des images. Les annexes sont incluses dans votre rapport final selon vos instructions. Cliquez 'Voir l'annexe' pour les consulter ou les modifier."
      },
      {
        q: "Comment exporter mes rapports ?",
        a: "Sur téléphone/ordinateur : Cliquez 'Exporter en Word'. Sur l'application bureau : choisissez 'Ouvrir dans le dossier par défaut' (s'ouvre automatiquement dans Word), 'Télécharger', ou 'Trier par date'. Formats disponibles : Word, PDF."
      },
      {
        q: "Voxia choisit-il automatiquement le bon modèle de rapport ?",
        a: "Oui, voxia analyse votre dictée et sélectionne automatiquement le modèle de rapport approprié. Vous pouvez toujours changer dans le dictaphone, ou après que le rapport soit généré en cliquant 'Changer le modèle' - un nouveau rapport sera généré. Les deux versions sont conservées."
      }
    ]
  },
  {
    category: 'Abonnement & facturation',
    icon: CreditCard,
    color: '#f39c12',
    questions: [
      {
        q: "Comment passer à un plan supérieur ?",
        a: "Dans Acceuil, cliquez sur « Gérer » puis « Gérer mon abonnement ». Le changement est immédiat et la différence de prix est calculée au prorata de la période restante."
      },
      {
        q: "Quelles fonctionnalités sont incluses dans le plan Gold ?",
        a: "Le plan Gold inclut : Dictée Live, création de macros, modèles de rapport personnalisés, instructions personnalisées par modèle de rapport, indentification automatique de l'adresse du destinataire  du rapportet accès prioritaire aux nouvelles fonctionnalités."
      },
      {
        q: "Puis-je suspendre temporairement mon abonnement ?",
        a: "Oui, dans Acceuil, cliquez sur « Gérer » puis « Gérer mon abonnement ». Ensuite, annuler votre abonnement. Votre accès reste actif jusqu'à la fin de la période déjà payée."
      },
      {
        q: "Comment sont gérés les paiements ?",
        a: "Les paiements sont sécurisés via Stripe. Nous acceptons les cartes bancaires, Twint, etc. Vous pouvez récupérer vos reçus sur votre espace client dans Acceuil > Gérer > Gérer mon abonnement."
      }
    ]
  },
  {
    category: 'Applications & compatibilité',
    icon: Smartphone,
    color: '#9b59b6',
    questions: [
      {
        q: "Sur quels systèmes puis-je installer l'application bureau ?",
        a: "Mac (MacOS 10.12 ou plus récent) et Windows (10/11). Fonctionnalités exclusives de l'application bureau : dictée en arrière-plan, raccourcis clavier personnalisables, insertion automatique dans vos logiciels, fenêtre flottante, capture d'écran, support des dictaphones Philips."
      },
      {
        q: "L'application mobile est-elle disponible ?",
        a: "Oui, disponible sur iPhone (App Store) et Android (Play Store). Vous pouvez enregistrer, transcrire, gérer vos rapports, prendre des photos, et vous connecter avec Face ID ou empreinte digitale. Vos données se synchronisent automatiquement entre tous vos appareils."
      },
      {
        q: "L'application mobile utilise-t-elle beaucoup de batterie ?",
        a: "Non. L'enregistrement est optimisé pour consommer peu d'énergie. La transcription se fait sur nos serveurs en Suisse, pas sur votre téléphone. L'application se met automatiquement en pause après 2 secondes de silence."
      },
      {
        q: "Mes Réglages se synchronisent-ils entre mes appareils ?",
        a: "Oui, tous vos Réglages se synchronisent automatiquement : langue, spécialité, macros (Gold+), raccourcis clavier, modèles personnalisés. Vos rapports récents (3 derniers mois) sont disponibles même sans connexion. Tous vos rapports sont toujours accessibles en ligne."
      },
      {
        q: "Puis-je utiliser voxia dans mon navigateur web ?",
        a: "Oui, https://voxia-md.ch fonctionne sur tout navigateur moderne."
      }
    ]
  },
  {
    category: 'Fonctionnalités avancées',
    icon: Rocket,
    color: '#e67e22',
    questions: [
      {
        q: "Comment créer et utiliser des macros ?",
        a: "Les macros sont des raccourcis texte (plan Gold/Entreprise requis). Création : Réglages > Macros > Ajouter un mot-clé court (ex: 'signature') et le texte complet à insérer. Utilisation sur l'application bureau : tapez le mot-clé et le texte complet apparaît automatiquement dans n'importe quel logiciel."
      },
      {
        q: "Comment personnaliser le formatage de mes rapports ?",
        a: "Avec le plan Gold : définissez des instructions spécifiques pour chaque modèle de rapport (style, format, niveau de détail). Cliquez sur l'icône crayon pour personnaliser. Vous pouvez aussi créer vos propres templates Word avec votre en-tête et logo dans Acceuil > Templates."
      },
      {
        q: "Qu'est-ce que l'annexe (photos) et comment l'utiliser ?",
        a: "Cette fonction extrait automatiquement le texte de vos images et documents. Cliquez sur le bouton appareil photo, prenez une photo ou importez un fichier (PDF, image, Word). Le texte est extrait, modifiable et inclus dans votre rapport. Idéal pour les résultats de labo, prescriptions et documents de référence."
      },
      {
        q: "Comment faire une capture d'écran avec extraction de texte ?",
        a: "Sur l'application bureau : appuyez sur F6 pour capturer tout l'écran, ou utilisez le bouton screenshot pour sélectionner une zone précise. Le texte est automatiquement extrait et ajouté à votre rapport. Vous pouvez personnaliser les raccourcis dans Réglages > Raccourcis clavier. Utile pour capturer des informations depuis d'autres logiciels."
      },
      {
        q: "Puis-je dicter en arrière-plan sur l'application bureau ?",
        a: "Oui. Démarrez l'enregistrement, puis réduisez la fenêtre - l'enregistrement continue. Vous pouvez travailler dans d'autres logiciels pendant votre dictée. Les raccourcis clavier fonctionnent même quand voxia est réduit. La qualité reste identique. Parfait pour les consultations et la documentation en temps réel."
      },
      {
        q: "Comment intégrer voxia à mon système informatique ?",
        a: "Pour les intégrations API et les connexions avec vos systèmes existants, contactez-nous via la page Contact. Nous proposons des solutions sur mesure pour les comptes Enterprise."
      }
    ]
  },
  {
    category: 'Sécurité & confidentialité',
    icon: Shield,
    color: '#27ae60',
    questions: [
      {
        q: "Où sont hébergées mes données ?",
        a: "Toutes vos données sont hébergées exclusivement en Suisse, dans des centres de données certifiés ISO 27001. Aucune donnée médicale n'est transférée hors de Suisse. Voxia respecte les normes RGPD, LPD et les exigences strictes de protection des données médicales."
      },
      {
        q: "Comment supprimer définitivement mes rapports ?",
        a: "Dans la liste des rapports, sélectionnez le rapport et cliquez sur « Supprimer ». Le rapport est immédiatement supprimé de la base de données active."
      },
      {
        q: "Mes données sont-elles utilisées pour entraîner l'IA générale de voxia ?",
        a: "Non, jamais. Vos rapports ne sont jamais utilisés pour entraîner les modèles d'IA généraux. Vous gardez un contrôle total sur vos données. Par contre, vos propres données servent à améliorer vos propores modèles, sans que d'autres utilisateurs ni que GBM Medical puisse les voir ou utiliser."
      },
      {
        q: "Comment fonctionne l'authentification biométrique ?",
        a: "Sur mobile, vous pouvez activer Face ID, Touch ID ou reconnaissance d'empreinte digitale pour un accès sécurisé rapide. Les données biométriques restent sur votre appareil et ne sont jamais transmises à nos serveurs."
      },
      {
        q: "Comment voxia sécurise-t-il mes données de connexion ?",
        a: "Voxia utilise un système de connexion sécurisée qui se rafraîchit automatiquement. Toutes les communications sont chiffrées avec plusieurs couches de sécurité. Vous pouvez voir tous vos appareils connectés et déconnecter ceux que vous ne reconnaissez pas dans Réglages > Gestion du compte > Sessions."
      }
    ]
  },
  {
    category: 'Compte & Réglages',
    icon: Settings,
    color: '#2c3e50',
    questions: [
      {
        q: "Comment changer mon adresse e-mail de connexion ?",
        a: "Allez dans Réglages > Gestion du compte > Email. Entrez votre nouvelle adresse. Vous recevrez des codes de vérification par email et SMS. Entrez les 2 codes plus votre code PIN à 6 chiffres, puis confirmez. Le changement est immédiat sur tous vos appareils."
      },
      {
        q: "Comment changer mon numéro de téléphone ?",
        a: "Réglages > Gestion du compte > Numéro de téléphone. Entrez votre nouveau numéro avec l'indicatif du pays (ex: +41790000000). Vous recevrez des codes par email et SMS sur le nouveau numéro. Entrez les 2 codes plus votre code PIN à 6 chiffres, puis confirmez."
      },
      {
        q: "Comment gérer mes sessions actives ?",
        a: "Réglages > Gestion du compte > Sessions de connexion. Vous voyez tous les appareils connectés (date de connexion). Pour supprimer un appareil : cliquez sur l'icône poubelle. Pour déconnecter tous les appareils sauf celui-ci : utilisez le bouton en haut. Déconnectez immédiatement les appareils suspects."
      },
      {
        q: "Comment supprimer mon compte ?",
        a: "ATTENTION - IRRÉVERSIBLE. Réglages > Gestion du compte > Supprimer votre compte. Vous recevrez un code par SMS. Entrez votre numéro, le code SMS et votre code PIN à 6 chiffres. Cliquez 'SUPPRIMER DÉFINITIVEMENT'. Toutes vos données (rapports, Réglages, macros) seront définitivement supprimées sans possibilité de récupération. Aucun remboursement possible."
      },
      {
        q: "Puis-je personnaliser mes raccourcis clavier ?",
        a: "Oui (Desktop uniquement). Réglages > Raccourcis clavier."
      }
    ]
  },
  {
    category: 'Support & assistance',
    icon: HelpCircle,
    color: '#8e44ad',
    questions: [
      {
        q: "Comment contacter le support voxia ?",
        a: "Utilisez la page Contact accessible depuis le menu principal pour toute question ou problème. Notre équipe répond généralement sous 48h (jours ouvrés)."
      },
      {
        q: "Où trouver de l'aide pour utiliser voxia ?",
        a: "La section Guide dans l'application contient des tutoriels complets sur toutes les fonctionnalités : dictée, types de rapport, photos, macros, instructions personnalisées, et plus encore."
      },
      {
        q: "Comment signaler un bug ou suggérer une amélioration ?",
        a: "Utilisez la page Contact en décrivant le problème ou votre suggestion. Nous apprécions tous les retours qui nous aident à améliorer voxia."
      },
      {
        q: "Y a-t-il une communauté d'utilisateurs voxia ?",
        a: "Nous travaillons à créer un espace d'échange pour les utilisateurs. En attendant, n'hésitez pas à nous contacter directement pour partager vos expériences et bonnes pratiques."
      }
    ]
  },
  {
    category: 'Templates & Export',
    icon: FileText,
    color: '#16a085',
    questions: [
      {
        q: "Puis-je utiliser mes propres templates Word ?",
        a: "Oui, téléchargez vos propres templates Word depuis Acceuil > Templates. Chaque template est lié à un modèle de rapport que vous choisissez."
      },
      {
        q: "Quels formats d'export sont disponibles ?",
        a: "Vous pouvez exporter vos rapports en Word (.docx) ou PDF. L'export préserve votre mise en forme et inclut votre logo personnalisé si vous en avez configuré un."
      },
      {
        q: "Comment personnaliser mes documents exportés ?",
        a: "Créez un modèle Word avec votre en-tête, logo, police et mise en forme préférés. Téléchargez-le dans Templates, et voxia l'utilisera automatiquement lors de l'export en préservant votre style professionnel."
      },
      {
        q: "Puis-je ajouter mon logo aux rapports ?",
        a: "Oui (plan Gold ou Enterprise requis), téléchargez votre logo depuis Réglages > Templates. Il sera automatiquement intégré dans tous vos documents exportés."
      }
    ]
  },
  {
    category: 'Fonctionnalités Enterprise',
    icon: FileText,
    color: '#2980b9',
    questions: [
      {
        q: "Comment gérer les utilisateurs de mon organisation ?",
        a: "Les administrateurs Enterprise peuvent inviter des utilisateurs, attribuer des rôles (admin/utilisateur standard), gérer les licences et consulter les statistiques d'utilisation depuis l'espace Administration Enterprise."
      },
      {
        q: "Puis-je créer des modèles de rapport pour toute mon équipe ?",
        a: "Oui, créez des types de rapport personnalisés au niveau entreprise avec des instructions spécifiques. Tous les membres de votre organisation y auront accès automatiquement, assurant une standardisation des documents."
      },
      {
        q: "Comment suivre l'utilisation de voxia dans mon entreprise ?",
        a: "L'espace Administration offre des statistiques détaillées par utilisateur, des journaux d'accès (audit trail), un suivi des licences utilisées/disponibles, et des exports Excel des rapports pour l'analyse (par ex. pour les statistiques d'une valeur clé)."
      },
      {
        q: "Comment inviter de nouveaux membres à mon organisation ?",
        a: "Dans Administration Enterprise > Utilisateurs, cliquez sur « Inviter un utilisateur ». Entrez l'adresse e-mail professionnelle, attribuez un rôle, et l'utilisateur recevra une invitation par e-mail pour rejoindre votre organisation."
      },
      {
        q: "Combien de templates peut avoir mon organisation ?",
        a: "Les comptes Enterprise peuvent créer jusqu'à 10 templates Word personnalisés au niveau organisation."
      }
    ]
  }
];

function FAQ() {
  const { isSubscribed, profile } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = isSubscribed || profile;
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFAQ, setFilteredFAQ] = useState(faqData);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredFAQ(faqData);
      return;
    }

    const filtered = faqData.map(category => ({
      ...category,
      questions: category.questions.filter(
        item => 
          item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.a.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.questions.length > 0);

    setFilteredFAQ(filtered);
  }, [searchTerm]);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    const newExpanded = new Set(expandedItems);
    
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    
    setExpandedItems(newExpanded);
  };

  const expandAll = () => {
    const allKeys = new Set();
    filteredFAQ.forEach((category, catIndex) => {
      category.questions.forEach((_, qIndex) => {
        allKeys.add(`${catIndex}-${qIndex}`);
      });
    });
    setExpandedItems(allKeys);
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  return (
    <div className="faq-page">
      {!isAuthenticated && <HeaderNoAuth />}
      {!isAuthenticated && <BackButton to="/landing" />}
      {isAuthenticated && <BackButton to="/guide" />}
      
      <div className="settings-container">
        {/* Search Section */}
        <Card className="card-large">
          <SectionHeader icon={HelpCircle} title="Foire aux questions" />
          
          <div className="faq-search-controls">
            <div className="faq-search-input-container">
              <Search className="faq-search-icon" />
              <input
                type="text"
                placeholder="Rechercher dans les questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="faq-search-input"
              />
            </div>
            <div className="faq-expand-controls">
              <Button 
                variant="secondary" 
                type="default"
                size="default"
                onClick={expandAll}
              >
                Tout déplier
              </Button>
              <Button 
                variant="secondary" 
                type="default"
                size="default"
                onClick={collapseAll}
              >
                Tout replier
              </Button>
            </div>
          </div>
        </Card>

        {/* FAQ Content */}
        {filteredFAQ.length === 0 ? (
          <Card className="card-large">
            <div className="faq-no-results">
              <HelpCircle size={48} className="faq-no-results-icon" />
              <h3>Aucun résultat trouvé</h3>
              <p>Aucun résultat trouvé pour "{searchTerm}"</p>
              <p>Essayez avec d'autres mots-clés</p>
              <Button 
                variant="primary" 
                type="default"
                onClick={() => setSearchTerm('')}
              >
                Effacer la recherche
              </Button>
            </div>
          </Card>
        ) : (
          filteredFAQ.map((category, categoryIndex) => {
            const Icon = category.icon;
            return (
              <Card key={categoryIndex} className="card-large">
                <div className="faq-category-header">
                  <div className="faq-category-icon" style={{ background: category.color }}>
                    <Icon size={24} />
                  </div>
                  <h3 className="faq-category-title">{category.category}</h3>
                </div>
                
                <div className="faq-questions-accordion">
                  {category.questions.map((item, questionIndex) => {
                    const isExpanded = expandedItems.has(`${categoryIndex}-${questionIndex}`);
                    
                    return (
                      <div 
                        key={questionIndex} 
                        className={`faq-item ${isExpanded ? 'expanded' : ''}`}
                      >
                        <button
                          className="faq-question"
                          onClick={() => toggleItem(categoryIndex, questionIndex)}
                        >
                          <span>{item.q}</span>
                          <ChevronDown className="faq-chevron" />
                        </button>
                        <div className="faq-answer">
                          <div className="faq-answer-content">
                            {item.a}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })
        )}

        {/* Contact Support */}
        <Card className="card-large">
          <div className="faq-contact-support">
            <Mail size={48} className="faq-contact-icon" />
            <h3>Vous n'avez pas trouvé votre réponse ?</h3>
            <p>Notre équipe support est là pour vous aider</p>
            <Button
              variant="primary"
              type="default"
              size="large"
              icon={Mail}
              onClick={() => navigate('/contact')}
            >
              Contacter le support
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default FAQ;